/**
 * GooglePayButton - Airwallex Google Pay Button Element
 * Renders Google Pay button when available in the current environment
 */
import { useCheckoutTracking } from '@checkout/core'
import { forwardRef, useState, useCallback, useEffect, useRef } from 'react'

import { buildAirwallexCustomer } from './billing'
import styles from './GooglePayButton.module.css'
import { useAirwallex } from './Provider'
import { Skeleton } from '../../components'
import { withRetry } from '../../utils'

import type { AirwallexElement } from './Provider'
import type { CheckoutPayerInput, PaymentInteractionOutcome } from '@checkout/core'

export interface GooglePayButtonProps {
  /** Payment Intent ID */
  intentId: string
  /** Client Secret */
  clientSecret: string
  /** Payment amount (decimal, e.g., 99.00) */
  amount: string
  /** Currency code (e.g., 'USD', 'HKD') */
  currency: string
  /** Merchant country code (e.g., 'HK', 'US') - Required for Google Pay */
  countryCode: string
  /** Merchant name displayed in Google Pay sheet */
  merchantName?: string
  /** Total price label displayed in cart modal */
  totalPriceLabel?: string
  /** Total price status: 'FINAL' for fixed subscription, 'NOT_CURRENTLY_KNOWN' for variable */
  totalPriceStatus?: 'NOT_CURRENTLY_KNOWN' | 'ESTIMATED' | 'FINAL'
  /** Button type */
  buttonType?:
    | 'buy'
    | 'pay'
    | 'plain'
    | 'order'
    | 'donate'
    | 'book'
    | 'checkout'
    | 'subscribe'
    | 'long'
    | 'short'
  /** Button color */
  buttonColor?: 'black' | 'white'
  /** Button size mode */
  buttonSizeMode?: 'fill' | 'static'
  onOutcome: (outcome: PaymentInteractionOutcome) => void
  /** Called when Google Pay availability is determined */
  onReady?: (available: boolean) => void
  /** Additional className */
  className?: string
  /** Whether this is a subscription payment */
  isSubscription?: boolean
  /** Customer ID for recurring payments (required when isSubscription is true) */
  customerId?: string
  /** Payer and billing context */
  payer?: CheckoutPayerInput
  /** Whether to require email from Google Pay */
  emailRequired?: boolean
  /** Whether to require billing address */
  billingAddressRequired?: boolean
}

/** Element retry configuration */
const ELEMENT_RETRY_CONFIG = {
  maxRetries: 2,
  timeout: 8000,
  baseDelay: 500,
  maxDelay: 2000,
}

export const GooglePayButton = forwardRef<HTMLDivElement, GooglePayButtonProps>(
  (
    {
      intentId,
      clientSecret,
      amount,
      currency,
      countryCode,
      merchantName = 'VigilTech Limited',
      totalPriceLabel = 'VigilTech Limited',
      totalPriceStatus = 'FINAL',
      buttonType = 'buy',
      buttonColor = 'black',
      buttonSizeMode = 'fill',
      onOutcome,
      onReady,
      className,
      isSubscription = false,
      customerId,
      payer,
      emailRequired = true,
      billingAddressRequired = true,
    },
    ref
  ) => {
    const { createElement, isReady, error: sdkError } = useAirwallex()
    const { trackPayment } = useCheckoutTracking()
    const elementRef = useRef<AirwallexElement | null>(null)
    const containerRef = useRef<HTMLDivElement>(null)
    const mountedRef = useRef(false)

    // 使用 refs 存储回调，避免 effect 重复执行
    const onOutcomeRef = useRef(onOutcome)
    const onReadyRef = useRef(onReady)
    const trackPaymentRef = useRef(trackPayment)

    // 更新 refs
    useEffect(() => {
      onOutcomeRef.current = onOutcome
      onReadyRef.current = onReady
      trackPaymentRef.current = trackPayment
    })

    const [isElementReady, setIsElementReady] = useState(false)
    const [_isAvailable, setIsAvailable] = useState<boolean | null>(null)
    const [isCreating, setIsCreating] = useState(false)
    const [error, setError] = useState<string | null>(null)

    // Cleanup element
    const cleanupElement = useCallback(() => {
      if (elementRef.current) {
        try {
          elementRef.current.unmount()
        } catch {
          // Ignore
        }
        elementRef.current = null
      }
      mountedRef.current = false
    }, [])

    // Initialize Google Pay Element
    const initializeElement = useCallback(async () => {
      if (!createElement || !containerRef.current || !intentId || !clientSecret) {
        return
      }

      cleanupElement()
      setIsElementReady(false)
      setIsCreating(true)
      setError(null)

      try {
        const elementOptions: Record<string, unknown> = {
          intent_id: intentId,
          client_secret: clientSecret,
          amount: {
            value: amount,
            currency: currency.toUpperCase(),
          },
          countryCode: countryCode.toUpperCase(),
          merchantInfo: {
            merchantName,
          },
          totalPriceLabel,
          totalPriceStatus,
          buttonType,
          buttonColor,
          buttonSizeMode,
          emailRequired,
          billingAddressRequired,
          billingAddressParameters: {
            format: 'MIN',
          },
        }

        // Subscription (recurring) payment
        if (isSubscription) {
          elementOptions.mode = 'recurring'
          elementOptions.customer_id = customerId
          elementOptions.payment_consent = {
            next_triggered_by: 'merchant',
            merchant_trigger_reason: 'scheduled',
          }
        }

        const customer = buildAirwallexCustomer(payer)
        if (customer) {
          elementOptions.customer = customer
        }

        const googlePayElement = await withRetry(async () => {
          return createElement('googlePayButton', elementOptions)
        }, ELEMENT_RETRY_CONFIG)

        // Listen for ready event - this includes availability check
        googlePayElement.on('ready', (data: unknown) => {
          const readyData = data as { canMakePayment?: boolean } | undefined
          const available = readyData?.canMakePayment ?? true
          setIsAvailable(available)
          setIsElementReady(true)
          onReadyRef.current?.(available)
          trackPaymentRef.current(available ? 'googlepay_available' : 'googlepay_unavailable')
        })

        // Listen for success event
        googlePayElement.on('success', (event: unknown) => {
          // Handle both event.detail format and direct data format
          const eventObj = event as { detail?: unknown } | undefined
          const data = eventObj?.detail ?? event
          trackPaymentRef.current('googlepay_submit_success')
          onOutcomeRef.current({
            status: 'succeeded',
            provider: 'airwallex',
            pmId: 'google_pay',
            raw: data,
          })
        })

        // Listen for error event
        googlePayElement.on('error', (event: unknown) => {
          // Handle both event.detail format and direct data format
          const eventObj = event as { detail?: unknown } | undefined
          const data = eventObj?.detail ?? event
          const errorData = data as { message?: string; error?: { message?: string } }
          const errorMessage =
            errorData?.message ?? errorData?.error?.message ?? 'Google Pay payment failed'
          trackPaymentRef.current('googlepay_submit_error', { error_message: errorMessage })
          setError(errorMessage)
          onOutcomeRef.current({
            status: 'failed',
            provider: 'airwallex',
            pmId: 'google_pay',
            error: errorMessage,
            raw: data,
          })
        })

        // Mount to DOM
        googlePayElement.mount('airwallex-googlepay-container')
        elementRef.current = googlePayElement
        mountedRef.current = true

        // Fallback: Mark as not available if no ready event after 3 seconds
        setTimeout(() => {
          if (!mountedRef.current) return
          setIsElementReady((prev) => {
            if (!prev) {
              setIsAvailable(false)
              onReadyRef.current?.(false)
              return true
            }
            return prev
          })
        }, 3000)
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to initialize Google Pay'
        setError(errorMessage)
        setIsAvailable(false)
        onReadyRef.current?.(false)
      } finally {
        setIsCreating(false)
      }
    }, [
      createElement,
      cleanupElement,
      intentId,
      clientSecret,
      amount,
      currency,
      countryCode,
      merchantName,
      totalPriceLabel,
      totalPriceStatus,
      buttonType,
      buttonColor,
      buttonSizeMode,
      isSubscription,
      customerId,
      payer,
      emailRequired,
      billingAddressRequired,
    ])

    useEffect(() => {
      if (!isReady || !createElement || mountedRef.current) {
        return
      }

      void initializeElement()

      return cleanupElement
    }, [isReady, createElement, initializeElement, cleanupElement])

    // Show error if SDK failed
    if (sdkError || error) {
      return null
    }

    // 始终显示 Airwallex 按钮，让 Airwallex SDK 自己处理点击后的不可用提示
    const showSkeleton = !isElementReady || isCreating

    return (
      <div ref={ref} className={`${styles.container} ${className ?? ''}`}>
        {showSkeleton && <Skeleton height="60px" style={{ borderRadius: '4px' }} />}
        <div
          ref={containerRef}
          id="airwallex-googlepay-container"
          className={styles.buttonWrapper}
          style={{ display: showSkeleton ? 'none' : undefined }}
        />
      </div>
    )
  }
)

GooglePayButton.displayName = 'GooglePayButton'
